import React, { useEffect, useState, useContext } from 'react'
import { ANALYSIS_STAGES, ANALYSIS_TYPES, bigPadding, getAssetLink, handleResponse } from '../common/utils'
import styled from 'styled-components'
import { cloneDeep } from 'lodash'
import i18n from 'live/services/i18n'
import { StatusBar } from './statusBar'
import { AssetTreeContext, ParamsContext } from '../qualityMonitoring/qualityMonitoring'
import { GeneralInfo } from './generalInfo'
import { QualityRating } from './qualityRating'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'
import { AnalysisResult } from './analysisResult'
import {
    analysisTop,
    convertRatingSetsIntoArrays,
    getActiveRatings,
    getFinalScore,
    getReturnPath,
    initialGeneralInfo,
    initialQualityRating,
    packagerPrefix,
    plansHaveEmptyFields
} from './analysisUtils'
import LiveTooltip from 'live/components/ui/tooltip'
import { postAnalysis } from '../common/apis'
import { GoBackBar } from '../common/goBackBar'
import { SaveFooter } from '../common/components'
import Scrollbars from 'react-scrollbars-custom'
import scrollbarStyles from '../common/scrollbarStyles'
import { EmptyFields } from './analysisTypes'

export const RatingContext = React.createContext<{
    ratingInfo: RatingInfo
    setRatingInfo: (info: RatingInfo) => void
    packagers: string[]
    activeRatings: RatingTypes[]
}>(null)
export const ResultContext = React.createContext<{
    generalInfo: GeneralInfo
    ratingInfo: RatingInfo | JSONConvertedRatingInfo
    actionPlans: ActionPlan[]
    activeRatings: RatingTypes[]
    setActionPlans: (plans: ActionPlan[]) => void
    finalScore: number
    analysisType: AnalysisTypes
}>(null)
interface IProps {
    analysisType: AnalysisTypes
}
export function QualityAnalysis({ analysisType }: IProps): JSX.Element {
    const urlParams = useContext(ParamsContext).split('/')
    const [currentStage, setCurrentStage] = useState<stagesTypes>(ANALYSIS_STAGES.GENERAL_INFO)
    const [generalInfo, setGeneralInfo] = useState<GeneralInfo>(initialGeneralInfo())
    const [ratingInfo, setRatingInfo] = useState<RatingInfo>(initialQualityRating())
    const [actionPlans, setActionPlans] = useState<ActionPlan[]>([])
    const [finalScore, setFinalScore] = useState(0)
    const [isSaving, setIsSaving] = useState(false)
    const [emptyFields, setEmptyFields] = useState<EmptyFields>({ fields: [], trigger: false })
    const assetTree = useContext(AssetTreeContext)

    const activeRatings = getActiveRatings(urlParams, assetTree)
    useEffect(() => {
        if (analysisType && !Object.values(ANALYSIS_TYPES).includes(analysisType)) {
            window.location.href = getAssetLink(urlParams[0])
        }
    }, [urlParams, analysisType])
    useEffect(() => {
        if (currentStage === ANALYSIS_STAGES.RESULT) {
            const ratingScores = activeRatings.map(rating => ratingInfo[rating].score)
            const scores = [...ratingScores, generalInfo.weighingScore]
            setFinalScore(getFinalScore(scores))
        } else if (currentStage === ANALYSIS_STAGES.RATING) {
            const updatedGeneralInfo = cloneDeep(generalInfo)
            updatedGeneralInfo.packagers = getPackagers(generalInfo.weighings)
            updatedGeneralInfo.participants = generalInfo.participants.filter(participant => participant)
            updatedGeneralInfo.analysisType = analysisType
            setGeneralInfo(updatedGeneralInfo)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStage])

    function disableSave(): boolean {
        if (analysisType === 'icm' || finalScore > 50) return false
        if (analysisType === 'icp' && !actionPlans.length) return false
        if (analysisType === 'icc' && !actionPlans.length) return true
        if (actionPlans.length && plansHaveEmptyFields(actionPlans)) return true
    }

    function getPackagers(weighings: IWeighing[]): string[] {
        return weighings.reduce((acc, weighing) => {
            if (weighing.packager !== packagerPrefix && !acc.includes(weighing.packager)) {
                acc.push(weighing.packager)
            }

            return acc
        }, [])
    }

    const saveDisabledStatus = disableSave()

    function handleAnalysisSubmit(): void {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [tab, country, factory, line, paramsProduct] = urlParams
        const typesArray = Object.values(ANALYSIS_TYPES) as string[]
        const product = typesArray.includes(paramsProduct) ? generalInfo.product : paramsProduct
        const ratingInfoConvertedForStringfy = convertRatingSetsIntoArrays(ratingInfo, generalInfo.packagers)
        const postParams = {
            ...generalInfo,
            ...ratingInfoConvertedForStringfy,
            actionPlans,
            finalScore,
            country,
            factory,
            line,
            product
        }
        setIsSaving(true)
        postAnalysis(postParams).then(postResponse => {
            handleResponse(postResponse, 'analysis')
            setIsSaving(false)
            if (postResponse.status === 200) window.location.href = getAssetLink(getReturnPath(urlParams))
        })
    }

    return (
        <StyledQualityAnalysis className={'qualityAnalysis'}>
            <GoBackBar />
            <div className="qualityAnalysis--contentContainer">
                <StatusBar
                    currentStage={currentStage}
                    setCurrentStage={setCurrentStage}
                    generalInfo={generalInfo}
                    ratingInfo={ratingInfo}
                    emptyFields={emptyFields}
                    setEmptyFields={setEmptyFields}
                />
                <div className="qualityAnalysis--contentContainer__scrollConfigs">
                    <Scrollbars
                        thumbYProps={scrollbarStyles.thumbYProps}
                        trackYProps={{ style: { ...scrollbarStyles.trackYProps.style, right: -15 } }}
                        disableTrackYWidthCompensation
                        removeTracksWhenNotUsed
                        mobileNative
                        noScrollX
                    >
                        <div className="scrollShadow">
                            <div className="scrollShadow--top" />
                            <div className="scrollShadow--top__cover" />
                            <div className="scrollShadow--bottom" />
                            <div className="scrollShadow--bottom__cover" />
                        </div>
                        <GeneralInfo
                            generalInfo={generalInfo}
                            setGeneralInfo={setGeneralInfo}
                            analysisType={analysisType}
                            renderConditional={currentStage === ANALYSIS_STAGES.GENERAL_INFO}
                            setEmptyFields={setEmptyFields}
                            emptyFields={emptyFields}
                        />

                        <RatingContext.Provider
                            value={{ ratingInfo, setRatingInfo, packagers: generalInfo.packagers, activeRatings }}
                        >
                            <QualityRating renderConditional={currentStage === ANALYSIS_STAGES.RATING} />
                        </RatingContext.Provider>
                        <ResultContext.Provider
                            value={{
                                generalInfo,
                                ratingInfo,
                                actionPlans,
                                setActionPlans,
                                finalScore,
                                analysisType,
                                activeRatings
                            }}
                        >
                            <AnalysisResult renderConditional={currentStage === ANALYSIS_STAGES.RESULT} />
                        </ResultContext.Provider>
                    </Scrollbars>
                </div>
            </div>
            {currentStage === ANALYSIS_STAGES.RESULT ? (
                <SaveFooter className={`qualityAnalysis--footer`}>
                    <LiveTooltip
                        position="top"
                        content={i18n('Please make sure the Action Plan is filled correctly')}
                        hide={!saveDisabledStatus}
                    >
                        <div>
                            <SubmitOrCancel
                                onCancel={() => (window.location.href = getAssetLink(getReturnPath(urlParams)))}
                                onSubmit={handleAnalysisSubmit}
                                saving={isSaving}
                                disabled={saveDisabledStatus}
                                buttonMessage={`${i18n('Finish')} ${analysisType?.toUpperCase()}`}
                            />
                        </div>
                    </LiveTooltip>
                </SaveFooter>
            ) : null}
        </StyledQualityAnalysis>
    )
}
const footerHeight = 80
const StyledQualityAnalysis = styled.div`
    .qualityAnalysis {
        &--contentContainer {
            padding: 0 ${bigPadding}px;

            .scrollShadow {
                --shadowHeight: 10px;
                position: absolute;
                width: 100%;
                height: 100%;
                pointer-events: none;
                z-index: 3;
                &--top,
                &--bottom {
                    position: sticky;
                    background: radial-gradient(
                        ellipse at center,
                        rgba(99, 62, 62, 0.25) 5%,
                        rgba(0, 0, 0, 0.25) 20%,
                        rgba(0, 0, 0, 0) 75%,
                        rgba(0, 0, 0, 0) 100%
                    );
                    &,
                    &__cover {
                        width: 100%;
                        height: var(--shadowHeight);
                        left: 0;
                    }
                    &__cover {
                        position: absolute;
                        z-index: 3;
                        background: var(--gray-0);
                    }
                }
                &--top {
                    transform: translateY(-50%);
                    &,
                    &__cover {
                        top: 0;
                    }
                }
                &--bottom {
                    &,
                    &__cover {
                        transform: translateY(50%);
                        top: calc(100% - var(--shadowHeight));
                    }
                }
            }
            &__scrollConfigs {
                height: calc(100vh - ${analysisTop + footerHeight}px);

                .ScrollbarsCustom-ThumbY {
                    opacity: 0;
                    transition: opacity 0.5s ease-in-out;
                }
                &:hover {
                    .ScrollbarsCustom-ThumbY {
                        opacity: 1;
                    }
                }
                .ScrollbarsCustom-Content {
                    position: relative;
                }
                .ScrollbarsCustom-Scroller {
                    padding-right: 1px;
                }
            }
        }
    }
`
